/* eslint-disable object-curly-newline */
import axios from 'axios';
// eslint-disable-next-line import/no-cycle
import router from '@/router';
import EventBus from '@/helpers/event-bus';
import * as mutations from '@/store/mutation-types';
import * as actions from '@/store/action-types';
import UserService from '@/api/user.service';
import JobService from '@/api/job.service';
import ZubtitleService from '@/api/zubtitle.service';
import ThemeService from '@/api/theme.service';
import AwsService from '@/api/aws.service';
import LinkService from '@/api/link.service';
import MailingListService from '@/api/mailing-list.service';
import { PLANS } from '@/config/account-config';
import { demoThemes } from '@/config/default-themes';
import * as events from '@/config/gtm-events';
import User from './models/User';

const signup = async ({ commit }, payload) => {
  try {
    const response = await axios.post('/api/users', payload);
    const { token } = response;
    localStorage.setItem('token', `Bearer ${token}`);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    commit(mutations.SET_AUTH_SUCCESS, token);
    const me = await UserService.getMe();
    const user = new User(me);
    commit(mutations.SET_USER, user);
    EventBus.$emit('trackGTM', events.newUser);
    EventBus.$emit('updateIntercom', user);
    EventBus.$emit('trackTapfiliate', user.id);
  } catch (e) {
    commit(mutations.SET_AUTH_ERROR);
    localStorage.removeItem('token');
  }
};

const syncUserToDb = async ({ commit }) => {
  try {
    let user;
    const me = await UserService.getMe();
    // check if stripe record exists
    if (me.stripe && me.stripe.id) {
      const sub = await UserService.getBillingStatus(me._id);
      user = new User(me, sub);
    } else {
      user = new User(me);
    }
    commit(mutations.SET_USER, user);
    EventBus.$emit('updateIntercom', user);
  } catch (err) {
    console.log('Error syncing user', err);
  }
};

const login = async ({ commit }, payload) => {
  commit(mutations.SET_AUTH_LOADING);

  try {
    const response = await axios.post('/auth', payload);
    const { token } = response;
    localStorage.setItem('token', `Bearer ${token}`);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    commit(mutations.SET_AUTH_SUCCESS, token);
    const me = await UserService.getMe();
    let user;
    // check if stripe record exists
    if (me.stripe && me.stripe.subscription) {
      const sub = await UserService.getBillingStatus(me._id);
      user = new User(me, sub);
    } else {
      user = new User(me);
    }
    commit(mutations.SET_USER, user);
    EventBus.$emit('loginBoot', me);
    EventBus.$emit('updateIntercom', me);
  } catch (e) {
    commit(mutations.SET_AUTH_ERROR);
    localStorage.removeItem('token');
  }
};

const logout = async ({ commit }) => {
  commit(mutations.SET_LOGOUT);
  EventBus.$emit('destroyIntercom');
  localStorage.removeItem('token');
  commit(mutations.SET_USER, {});
  delete axios.defaults.headers.common.Authorization;
  router.push('/login');
};

// Save canvas for history
const saveCanvas = async ({ commit }, canvas) => {
  commit(mutations.SAVE_CANVAS, canvas);
};

const clearCanvas = async ({ commit }) => {
  commit(mutations.CLEAR_CANVAS);
};

// AUDIO ACTIONS
const setAudioFile = ({ dispatch, commit }, file) => {
  if (file) {
    const urlReader = new FileReader();
    urlReader.addEventListener('load', () => {
      commit(mutations.SET_AUDIO_DATA_URL, urlReader.result);
    });
    urlReader.readAsDataURL(file);

    const arrayBufferReader = new FileReader();
    arrayBufferReader.addEventListener('load', () => {
      commit(mutations.SET_AUDIO_BUFFER, arrayBufferReader.result);
      dispatch(actions.DECODE_AUDIO_BUFFER);
    });
    arrayBufferReader.readAsArrayBuffer(file);
  }
};

const decodeAudioBuffer = async ({ state, commit }) => {
  commit(mutations.SET_AUDIO_BUFFER_DECODING, true);
  if (state.audioBuffer) {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtx.decodeAudioData(
      state.audioBuffer,
      (audioBuffer) => {
        commit(mutations.SET_AUDIO_BUFFER_DECODED, audioBuffer);
        commit(mutations.SET_AUDIO_BUFFER_DECODING, false);
      },
      (err) => {
        console.log('Error decoding audio:', err);
      }
    );
  }
};

const clearAudioFile = ({ commit }) => {
  commit(mutations.CLEAR_AUDIO_DATA);
};

const jobUpdate = async ({ commit }, { id, data }) => {
  try {
    await JobService.update(id, data);
    commit(mutations.SET_JOB_DATA, { id, data });
  } catch (err) {
    console.log('Error updating job', err);
  }
};

// eslint-disable-next-line
const jobCreate = async (
  { state, commit, dispatch },
  { uuid, start, end, title = 'My Video', themeId, isDemoTheme }
) => {
  EventBus.$emit('trackGTM', events.createVideo);
  try {
    // add { status: pending, specs, showWatermark }
    // create the job object in the DB
    // fetch the theme specs from the state
    // trigger the lambda job runner
    // trigger status polling
    let theme;
    if (isDemoTheme) {
      theme = demoThemes.find(t => t._id === themeId);
    } else {
      theme = await ThemeService.get(themeId);
      delete theme.objects; // we don't want to store fabric obj
    }
    const watermark = state.user.products.video === PLANS.FREE;
    const duration = (end - start).toFixed(2);
    const specs = Object.assign({}, theme, { start, end, duration, watermark });

    const newJob = await UserService.createJob(state.user.id, {
      guid: uuid,
      title,
      status: 'pending',
      specs
    });
    const themeData = {
      uuid,
      duration,
      watermark,
      width: theme.width,
      height: theme.height,
      waveform: theme.waveform,
      waveColor: theme.waveColor,
      waveTop: theme.waveTop,
      waveBottom: theme.waveBottom,
      waveLeft: theme.waveLeft,
      waveRight: theme.waveRight,
      progressBar: theme.progressBar,
      progressBarType: theme.progressBarType,
      progressBarColor: theme.progressBarColor,
      progressBarSecondaryColor: theme.progressBarSecondaryColor,
      progressBarTop: theme.progressBarTop,
      progressBarBottom: theme.progressBarBottom,
      progressBarLeft: theme.progressBarLeft,
      progressBarRight: theme.progressBarRight,
      backgroundImage: theme.backgroundImage, // looks in s3:wavvegram/background/{backgroundImage}
      pattern: theme.pattern,
      backgroundColor: theme.backgroundColor || 'white',
      samplesPerFrame: 128
    };
    AwsService.startLambda({ uuid, start, end, theme: themeData });
    commit(mutations.CLEAR_AUDIO_DATA);
    dispatch(actions.POLL_VIDEO_CREATION, newJob._id);
    await dispatch(actions.SYNC_USER_TO_DB);
  } catch (err) {
    console.log('Error creating job', err);
  }
};

const pollVideoCreation = async ({ dispatch, commit }, id) => {
  try {
    const maxProcessingTime = 1 * 60 * 60 * 1000; // one hour (60m/h * 60s/m * 1000ms/s)
    // const status = AwsService.getVideoRenderingStatus({ uuid, duration })
    const job = await JobService.get(id);
    if (job.status === 'complete') {
      // TODO - Distinguish between job.status checks and status.stage checks
      // eslint-disable-next-line
      commit(mutations.SET_JOB_DATA, {
        id,
        data: { status: 'complete', url: `https://s3.amazonaws.com/sls-wavve-out/${job.guid}.mp4` }
      });
      await dispatch(actions.JOB_UPDATE, { id, data: { status: 'complete' } });
      dispatch(actions.SYNC_USER_TO_DB);
      return;
    }
    const status = await JobService.checkStatus(id, {
      duration: +job.specs.duration,
      uuid: job.guid
    });
    console.log('status', status);
    if (!status || status.error !== undefined) {
      console.log('Error polling lambda video', status.error);
      return;
    }
    if (status.stage === 'complete') {
      console.log('status.state === complete', status.stage);
      // lambda will update the job.status to 'complete'
      // sync with the database to update the time remaining, etc.
      // eslint-disable-next-line
      commit(mutations.SET_JOB_DATA, {
        id,
        data: { status: 'complete', url: `https://s3.amazonaws.com/sls-wavve-out/${job.guid}.mp4` }
      });
      await dispatch(actions.JOB_UPDATE, { id, data: { status: 'complete' } });
      dispatch(actions.SYNC_USER_TO_DB);
      // return { status: 'complete' }
    } else {
      if (new Date(job.date_added) < Date.now() - maxProcessingTime) {
        // job took too long, mark as failed (used for not polling on videos page)
        dispatch(actions.JOB_UPDATE, { id: job._id, data: { status: 'failed' } });
      }
      // this data is set locally, but we don't need to update DB since status is already 'pending'
      commit(mutations.SET_JOB_DATA, {
        id,
        data: { detailedStatus: status }
      });
      // not available yet, poll again in 5 seconds
      setTimeout(() => {
        dispatch(actions.POLL_VIDEO_CREATION, id);
      }, 5000);
    }
  } catch (e) {
    console.log('Error polling lambda video', e);
  }
};

const pollZubtitleTranscription = async ({ dispatch }, guid) => {
  try {
    const job = await JobService.findByGuid(guid);
    if (!job) {
      return null;
    }
    if (job.transcript && job.transcript.finished) {
      return { status: 'complete' };
    }
    const doc = await ZubtitleService.get(guid);
    if (doc && doc.exists) {
      const transcriptionData = doc.data;
      if (transcriptionData.captions) {
        // we have a transcript!
        await dispatch(actions.JOB_UPDATE, {
          id: job._id,
          data: { transcript: { started: true, finished: true } }
        });
        return { status: 'complete' };
      }
      // not available yet, poll again in 5 seconds
      setTimeout(() => {
        dispatch(actions.POLL_ZUBTITLE_TRANSCRIPTION, guid);
      }, 5000);
      return { status: 'pending' };
    }
    // no doc found
    return null;
  } catch (e) {
    console.log('Error polling transcription', e);
    return null;
  }
};

const pollZubtitleFinalization = async ({ commit, dispatch }, guid) => {
  try {
    const job = await JobService.findByGuid(guid);
    if (!job) {
      return null;
    }
    if (job.subtitles && job.subtitles.ref) {
      // finished and uploaded to wavve
      // make sure that vuex data matches
      const { ref } = job.subtitles;
      // eslint-disable-next-line
      await commit(mutations.SET_JOB_DATA, {
        id: job._id,
        data: {
          subtitles: {
            started: true,
            finished: true,
            ref,
            captionUrl: `https://s3.amazonaws.com/subtitles-out/${ref}`
          }
        }
      });
      return { status: 'complete' };
    }
    const progressResponse = await ZubtitleService.getProgress(guid);
    const { status, progress } = progressResponse;
    if (status === 'complete') {
      // upload to S3 from zubtitle, this will also write to mongo db
      await ZubtitleService.upload(guid);
      // write to vuex state, manually set captionUrl
      const ref = `${job.guid}.mp4`;
      // eslint-disable-next-line
      await commit(mutations.SET_JOB_DATA, {
        id: job._id,
        data: {
          subtitles: {
            started: true,
            finished: true,
            ref,
            captionUrl: `https://s3.amazonaws.com/subtitles-out/${ref}`
          }
        }
      });
      JobService.update(job._id, { subtitles: { started: true, finished: true, ref } });
      return { status: 'complete' };
    }
    commit(mutations.SET_JOB_DATA, {
      id: job._id,
      data: { subtitles: { started: true, finished: false, progress } }
    });

    setTimeout(() => {
      dispatch(actions.POLL_ZUBTITLE_FINALIZATION, guid);
    }, 5000);
    return { status: 'pending', progress };
  } catch (err) {
    console.log('Error polling zubtitle finalization', err);
    return null;
  }
};

const createWavveLink = async ({ commit }) => {
  try {
    const link = await LinkService.create();
    commit(mutations.SET_USER_DATA, { link: { shortId: link.link.shortId } });
    EventBus.$emit('trackIntercomEvent', 'wavvelink_created');
    EventBus.$emit('trackGTM', events.createWavveLink);
    return link;
  } catch (e) {
    console.log('Error creating wavve link', e);
    return null;
  }
};

const updateWavveLink = async ({ state, commit }, update) => {
  await LinkService.update(update);
  commit(mutations.SET_USER_DATA, { link: { ...state.user.link, ...update } });
};

const deleteTheme = async ({ state, commit }, id) => {
  try {
    // send HTTP request
    await ThemeService.delete(id, { deleted: true });
    // set vuex state
    const filteredThemes = state.user.themes.filter(theme => theme.id !== id);
    commit(mutations.SET_USER_DATA, { themes: filteredThemes, canCreateNewDesign: true });
  } catch (err) {
    console.log('Error deleting theme', err);
  }
};

const fetchMailingListSubscribers = async (_, listOwnerId) => MailingListService.listSubscribers(listOwnerId);

const subscribeToMailingList = async (_, { listOwnerId, email }) => MailingListService.subscribe(listOwnerId, { email });

const setCustomDomain = async ({ state, commit }, { customDomain }) => {
  const link = { ...state.user.link, customDomain };
  await LinkService.update(link);
  commit(mutations.SET_USER_DATA, { link });
};

const removeCustomDomain = async ({ state, commit }) => {
  const link = { ...state.user.link, customDomain: null };
  await LinkService.update(link);
  commit(mutations.SET_USER_DATA, { link });
};

export default {
  [actions.SIGNUP]: signup,
  [actions.LOGIN]: login,
  [actions.LOGOUT]: logout,
  [actions.SAVE_CANVAS]: saveCanvas,
  [actions.CLEAR_CANVAS]: clearCanvas,
  [actions.SET_AUDIO_FILE]: setAudioFile,
  [actions.DECODE_AUDIO_BUFFER]: decodeAudioBuffer,
  [actions.CLEAR_AUDIO_FILE]: clearAudioFile,
  [actions.JOB_UPDATE]: jobUpdate,
  [actions.JOB_CREATE]: jobCreate,
  [actions.POLL_VIDEO_CREATION]: pollVideoCreation,
  [actions.SYNC_USER_TO_DB]: syncUserToDb,
  [actions.POLL_ZUBTITLE_FINALIZATION]: pollZubtitleFinalization,
  [actions.POLL_ZUBTITLE_TRANSCRIPTION]: pollZubtitleTranscription,
  [actions.CREATE_WAVVE_LINK]: createWavveLink,
  [actions.UPDATE_WAVVE_LINK]: updateWavveLink,
  [actions.DELETE_THEME]: deleteTheme,
  [actions.FETCH_MAILING_LIST_SUBSCRIBERS]: fetchMailingListSubscribers,
  [actions.SUBSCRIBE_TO_MAILING_LIST]: subscribeToMailingList,
  [actions.SET_CUSTOM_DOMAIN]: setCustomDomain,
  [actions.REMOVE_CUSTOM_DOMAIN]: removeCustomDomain
};
